// /src/data/tools.js
export const tools = [
  {
    name: 'HADES',
    slug: 'hades',
    tagline: 'Metadata Forensics Engine',
    description:
      'Hidden Artifact Detection & EXIF Scanner: a 153k-line metadata forensics platform that detects threats traditional AV misses. Scans file metadata across images, documents, archives, and firmware without executing target files. YARA pattern matching, ML ensemble detection, and behavioral analysis feed a unified 0-100 threat score mapped to 51+ MITRE ATT&CK techniques.',
    status: 'Production',
    features: [
      'YARA + ML ensemble detection pipeline',
      'MITRE ATT&CK mapping (51+ techniques)',
      'Court-ready evidence chains',
      '10 automated response playbooks',
      'REST API + Web Dashboard + CLI',
      'Kubernetes & Docker deployment',
      'SIEM export (CEF, STIX, ECS, Syslog)'
    ],
    docs: true,
    docsUrl: '/docs/hades-onepager.pdf',
    demo: true,
    demoUrl: 'https://hades.darkhorseinfosec.com',
    // Detail page data
    metrics: [
      { value: '153k+', label: 'Lines of Python' },
      { value: '90', label: 'Core Modules' },
      { value: '87', label: 'Test Suites' },
      { value: '51+', label: 'MITRE Techniques' },
    ],
    techStack: [
      'Python 3.8+', 'FastAPI', 'YARA', 'scikit-learn', 'XGBoost', 'ExifTool',
      'PostgreSQL', 'Redis', 'Docker', 'Kubernetes', 'Prometheus', 'Grafana',
    ],
    architecture: [
      { layer: 'Input', detail: 'CLI, REST API file upload, email gateway, file system monitor, batch ingestion' },
      { layer: 'Extraction', detail: 'ExifTool persistent process pool normalizes metadata across images, documents, archives, video, audio, and firmware' },
      { layer: 'Detection Pipeline', detail: 'YARA pattern matching (8 rule sets) \u2192 Heuristic analysis \u2192 Deep format analysis (PDF, Office, SVG) \u2192 ML ensemble (Isolation Forest + Random Forest + XGBoost) \u2192 Behavioral/campaign analysis \u2192 Firmware threat detection' },
      { layer: 'Scoring', detail: 'Composite 0-100 threat score with severity levels (Critical, High, Medium, Low, Info) mapped to MITRE ATT&CK techniques' },
      { layer: 'Output', detail: 'CLI reports (JSON/CSV/HTML), REST API, interactive React dashboard, SIEM export (CEF, STIX, ECS, Syslog, LEEF), hash-chained evidence logs' },
      { layer: 'Automation', detail: '10 playbooks (phishing triage, BEC detection, malware workflow, ransomware response, insider threat, DLP, campaign escalation)' },
    ],
    whyItMatters:
      'Most security tools analyze files through execution (dynamic sandboxes) or signature matching (AV). HADES specializes in static, non-execution metadata analysis, the layer where attackers hide malware, steganography, payload injection, and exfiltration attempts. Safe by design: no execution risk, suitable for air-gapped and high-security environments.',
  },
  {
    name: 'VitalChain Health',
    slug: 'vitalchain',
    tagline: 'Blockchain Healthcare Platform',
    description:
      'Unified Hyperledger Fabric platform combining emergency medical records and Medicaid eligibility verification. A 3-org blockchain network gives first responders instant, role-appropriate access to critical patient data, even offline, while AI-powered fraud detection secures Medicaid benefit verification. Currently in AWS pilot with 100 synthetic patients and 10 responder IDs.',
    status: 'Pilot',
    features: [
      '3-org Hyperledger Fabric network (6 peers, 3 orderers)',
      'HL7 FHIR R4 interoperability',
      '269 Go chaincode tests, 297 integration tests',
      'AI-powered Medicaid fraud detection',
      'Offline-first with encrypted cache',
      '14 responder role types with tiered RBAC',
      'HIPAA-aligned audit trails',
      'AWS pilot deployment (pilot.vitalchainhealth.com)'
    ],
    docs: true,
    docsUrl: '/docs/vitalchain-public-docs.pdf',
    secondaryDocs: { label: 'MedicaidChain Docs', url: '/docs/medicaidchain-public-docs.pdf' },
    demo: false,
    demoUrl: null,
    // Detail page data
    metrics: [
      { value: '269', label: 'Go Chaincode Tests' },
      { value: '297', label: 'Integration Tests' },
      { value: '3', label: 'Blockchain Orgs' },
      { value: '14', label: 'Responder Roles' },
    ],
    techStack: [
      'Hyperledger Fabric 2.5', 'Go 1.22', 'TypeScript', 'Node.js 18', 'React 18',
      'HL7 FHIR R4', 'TensorFlow.js', 'CouchDB', 'Docker', 'Turborepo', 'MUI 5',
    ],
    architecture: [
      { layer: 'Blockchain', detail: '3 organizations (Hospital, Emergency Services, Medicaid Agency) with 6 peers, 3 etcdRaft orderers, and 2 isolated channels (medical records + Medicaid eligibility)' },
      { layer: 'Smart Contracts', detail: '3 Go chaincodes: medical records (10.7k lines), offline verification, and Medicaid eligibility, all with comprehensive test coverage' },
      { layer: 'Backend', detail: 'TypeScript/Node.js API layer with 6 shared packages (@vch/auth, @vch/blockchain, @vch/types, @vch/logging, @vch/config, @vch/fraud-framework)' },
      { layer: 'Access Control', detail: '14 responder types (Paramedic EMT-Basic/-Advanced/-Critical, Firefighter, Law Enforcement, Coast Guard, Search & Rescue) across 4 access tiers + emergency override' },
      { layer: 'Interoperability', detail: 'HL7 FHIR R4 bidirectional mapping, SMART on FHIR backend services, Patient CRUD, AllergyIntolerance, MedicationStatement, Condition resources' },
      { layer: 'Fraud Detection', detail: 'TensorFlow.js neural networks, isolation forest, statistical analysis, and 7 rule-based rules for Medicaid benefit verification' },
    ],
    whyItMatters:
      'When a paramedic arrives on scene, they have zero access to critical patient medical history: allergies, medications, conditions. That gap costs lives. VitalChain puts patients in control of their data on a permissioned blockchain while giving first responders instant, role-appropriate access, even offline in rural areas with no connectivity.',
  }
];